/**
 * Low Stock Alert Popup — Super Admin notification for products below threshold.
 * Shows as a dismissible popup when stock falls below 20 units.
 * Includes one-click restock functionality.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import { AlertTriangle, X, Package, ChevronRight, RefreshCw, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const LOW_STOCK_THRESHOLD = 20;

export default function LowStockAlert() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissed_stock_alerts') || '[]'); } catch { return []; }
  });
  const [minimized, setMinimized] = useState(true);

  // Restock dialog state
  const [restockProduct, setRestockProduct] = useState(null);
  const [restockQty, setRestockQty] = useState(20);
  const [restockNotes, setRestockNotes] = useState('');
  const [restocking, setRestocking] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const loadAlerts = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const [alertsRes, lowStockRes] = await Promise.all([
        api.request('/equipment/alerts'),
        api.request(`/equipment/low-stock?threshold=${LOW_STOCK_THRESHOLD}`),
      ]);
      setAlerts(alertsRes || []);
      setLowStockProducts(lowStockRes || []);

      const undismissed = (lowStockRes || []).filter(p => !dismissed.includes(p.id));
      if (undismissed.length > 0) {
        setVisible(true);
      }
    } catch (err) {
      // Silently fail — alerts are non-critical
    }
  }, [isSuperAdmin, dismissed]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // Poll every 60 seconds
  useEffect(() => {
    if (!isSuperAdmin) return;
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, [isSuperAdmin, loadAlerts]);

  const dismissAll = () => {
    const ids = lowStockProducts.map(p => p.id);
    setDismissed(ids);
    sessionStorage.setItem('dismissed_stock_alerts', JSON.stringify(ids));
    setVisible(false);
    setExpanded(false);
  };

  const dismissSingle = async (alertId, productId) => {
    try {
      if (alertId) {
        await api.request(`/equipment/alerts/${alertId}/dismiss`, { method: 'PUT' });
      }
    } catch {}
    const newDismissed = [...dismissed, productId];
    setDismissed(newDismissed);
    sessionStorage.setItem('dismissed_stock_alerts', JSON.stringify(newDismissed));
    setLowStockProducts(prev => prev.filter(p => p.id !== productId));
    if (lowStockProducts.length <= 1) {
      setVisible(false);
    }
  };

  const handleRestock = async () => {
    if (!restockProduct || restockQty < 1) return;
    setRestocking(true);
    try {
      await api.request(`/equipment/restock/${restockProduct.id}`, {
        method: 'POST',
        body: JSON.stringify({ quantity: restockQty, notes: restockNotes }),
      });
      toast.success(`Added ${restockQty} units to ${restockProduct.product_name}`);
      setRestockProduct(null);
      setRestockQty(20);
      setRestockNotes('');
      // Clear dismissed cache so we reload fresh data
      sessionStorage.removeItem('dismissed_stock_alerts');
      setDismissed([]);
      loadAlerts();
    } catch (err) {
      toast.error(err.message || 'Failed to restock');
    }
    setRestocking(false);
  };

  if (!isSuperAdmin || !visible) return (
    <>
      {/* Restock dialog can still be rendered */}
      <RestockDialog
        product={restockProduct}
        qty={restockQty}
        setQty={setRestockQty}
        notes={restockNotes}
        setNotes={setRestockNotes}
        onRestock={handleRestock}
        onClose={() => setRestockProduct(null)}
        restocking={restocking}
      />
    </>
  );

  const undismissed = lowStockProducts.filter(p => !dismissed.includes(p.id));
  if (undismissed.length === 0) return null;

  if (minimized) {
    return (
      <>
        <button
          onClick={() => setMinimized(false)}
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg px-4 py-2.5 transition-colors"
          data-testid="low-stock-alert-badge"
        >
          <AlertTriangle className="h-4 w-4" />
          <span className="text-xs font-semibold">{undismissed.length} Low Stock</span>
        </button>
        <RestockDialog
          product={restockProduct}
          qty={restockQty}
          setQty={setRestockQty}
          notes={restockNotes}
          setNotes={setRestockNotes}
          onRestock={handleRestock}
          onClose={() => setRestockProduct(null)}
          restocking={restocking}
        />
      </>
    );
  }

  return (
    <>
      <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full" data-testid="low-stock-alert-popup">
        <div className="bg-card border-2 border-orange-300 rounded-xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-orange-50 px-4 py-3 flex items-center justify-between border-b border-orange-200">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-orange-900">Low Stock Warning</p>
                <p className="text-[10px] text-orange-600">{undismissed.length} product{undismissed.length !== 1 ? 's' : ''} below {LOW_STOCK_THRESHOLD} units</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMinimized(true)}
                className="p-1 rounded hover:bg-orange-100 text-orange-400 hover:text-orange-600 transition-colors"
                data-testid="minimize-low-stock-alert"
                title="Minimize"
              >
                <ChevronRight className="h-4 w-4 rotate-90" />
              </button>
              <button
                onClick={dismissAll}
                className="p-1 rounded hover:bg-orange-100 text-orange-400 hover:text-orange-600 transition-colors"
                data-testid="dismiss-all-alerts"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Products List */}
          <div className={`overflow-hidden transition-all duration-300 ${expanded ? 'max-h-96' : 'max-h-48'}`}>
            <div className="divide-y">
              {undismissed.slice(0, expanded ? 20 : 3).map((product) => {
                const matchingAlert = alerts.find(a => a.product_id === product.id);
                const stockPct = Math.min((product.quantity_in_stock / LOW_STOCK_THRESHOLD) * 100, 100);
                const isCritical = product.quantity_in_stock <= 5;
                return (
                  <div
                    key={product.id}
                    className="px-4 py-2.5 flex items-center gap-2.5 hover:bg-muted/30 transition-colors"
                    data-testid={`low-stock-item-${product.id}`}
                  >
                    <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {product.image_url ? (
                        <img src={product.image_url} alt="" className="w-full h-full object-contain p-0.5" />
                      ) : (
                        <Package className="h-4 w-4 text-muted-foreground/40" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{product.product_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-muted-foreground">{product.platform}</span>
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[60px]">
                          <div
                            className={`h-full rounded-full ${isCritical ? 'bg-red-500' : 'bg-orange-400'}`}
                            style={{ width: `${stockPct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <span className={`text-xs font-bold flex-shrink-0 ${isCritical ? 'text-red-600' : 'text-orange-600'}`}
                      data-testid={`stock-count-${product.id}`}
                    >
                      {product.quantity_in_stock} left
                    </span>
                    {/* Restock Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setRestockProduct(product); setRestockQty(LOW_STOCK_THRESHOLD - (product.quantity_in_stock || 0) + 10); }}
                      className="p-1 rounded-md bg-green-50 text-green-700 hover:bg-green-100 transition-colors flex-shrink-0"
                      title="Quick Restock"
                      data-testid={`restock-btn-${product.id}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => dismissSingle(matchingAlert?.id, product.id)}
                      className="p-0.5 text-muted-foreground/40 hover:text-muted-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t bg-muted/20 flex items-center justify-between">
            {undismissed.length > 3 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-[10px] text-primary font-medium hover:underline"
              >
                {expanded ? 'Show less' : `+${undismissed.length - 3} more`}
              </button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7 ml-auto"
              onClick={() => { navigate('/admin/hardware-markup'); setVisible(false); }}
              data-testid="go-to-inventory-btn"
            >
              Manage Stock <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Restock Dialog */}
      <RestockDialog
        product={restockProduct}
        qty={restockQty}
        setQty={setRestockQty}
        notes={restockNotes}
        setNotes={setRestockNotes}
        onRestock={handleRestock}
        onClose={() => setRestockProduct(null)}
        restocking={restocking}
      />
    </>
  );
}

function RestockDialog({ product, qty, setQty, notes, setNotes, onRestock, onClose, restocking }) {
  if (!product) return null;
  return (
    <Dialog open={!!product} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="restock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-green-600" /> Quick Restock
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
              {product.image_url ? (
                <img src={product.image_url} alt="" className="w-full h-full object-contain p-1" />
              ) : (
                <Package className="h-5 w-5 text-muted-foreground/40" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold">{product.product_name}</p>
              <p className="text-xs text-muted-foreground">{product.platform} - Current stock: <span className="font-medium text-orange-600">{product.quantity_in_stock}</span></p>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Units to Add</label>
            <div className="flex items-center gap-2 mt-1">
              {[10, 20, 50, 100].map(q => (
                <button
                  key={q}
                  onClick={() => setQty(q)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    qty === q ? 'bg-green-50 border-green-300 text-green-700' : 'border-border hover:border-green-200'
                  }`}
                  data-testid={`restock-preset-${q}`}
                >
                  +{q}
                </button>
              ))}
              <Input
                type="number"
                min={1}
                value={qty}
                onChange={e => setQty(parseInt(e.target.value) || 1)}
                className="w-20"
                data-testid="restock-qty-input"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              New stock will be: <span className="font-medium text-green-700">{(product.quantity_in_stock || 0) + qty}</span> units
            </p>
          </div>
          <div>
            <label className="text-sm font-medium">Notes (optional)</label>
            <Input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="mt-1"
              placeholder="e.g. Reorder from supplier #123"
              data-testid="restock-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={onRestock}
            disabled={restocking || qty < 1}
            className="bg-green-600 hover:bg-green-700"
            data-testid="confirm-restock-btn"
          >
            {restocking ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Restocking...</>
            ) : (
              <><Plus className="h-4 w-4 mr-2" /> Add {qty} Units</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
